import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { apiWithAuth } from '../api';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Wallpaper as WallpaperIcon,
} from '@mui/icons-material';

const CreateOrganizerProfile = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    organization_name: '',
    contact_info: '',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState(null);
  const profileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverPhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoverPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();
      submitData.append('organization_name', formData.organization_name);
      if (formData.contact_info) {
        submitData.append('contact_info', formData.contact_info);
      }
      if (profilePicture) {
        submitData.append('profile_picture', profilePicture);
      }
      if (coverPhoto) {
        submitData.append('cover_photo', coverPhoto);
      }

      await apiWithAuth(token).post('/organizer/profile', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // Invalidate organizer profile query to refresh the cache
      queryClient.invalidateQueries(['organizerProfile']);
      history.push('/organizer-profile');
    } catch (error) {
      console.error('Error creating profile:', error);
      // Handle validation errors
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          setError(error.response.data.detail);
        } else if (Array.isArray(error.response.data.detail)) {
          // Handle validation errors array
          setError(error.response.data.detail.map(err => err.msg).join(', '));
        } else {
          setError('Failed to create profile. Please try again.');
        }
      } else {
        setError('Failed to create profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Create Organizer Profile
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Cover Photo Upload */}
            <Box sx={{ mb: 3, position: 'relative' }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Cover Photo
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: 200,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: 'grey.100',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'grey.200',
                  },
                }}
                onClick={() => coverInputRef.current?.click()}
              >
                {coverPhotoPreview ? (
                  <img
                    src={coverPhotoPreview}
                    alt="Cover preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <WallpaperIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Click to upload cover photo
                    </Typography>
                  </Box>
                )}
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    coverInputRef.current?.click();
                  }}
                >
                  <WallpaperIcon />
                </IconButton>
              </Box>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={coverInputRef}
                onChange={handleCoverPhotoChange}
              />
            </Box>

            {/* Profile Picture Upload */}
            <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Profile Picture
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={profilePicturePreview || undefined}
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'primary.main',
                  }}
                >
                  <AccountCircleIcon sx={{ fontSize: 80 }} />
                </Avatar>
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                  onClick={() => profileInputRef.current?.click()}
                >
                  <AccountCircleIcon fontSize="small" />
                </IconButton>
              </Box>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={profileInputRef}
                onChange={handleProfilePictureChange}
              />
            </Box>

            <TextField
              fullWidth
              label="Organization Name"
              name="organization_name"
              value={formData.organization_name}
              onChange={handleChange}
              required
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Contact Information"
              name="contact_info"
              value={formData.contact_info}
              onChange={handleChange}
              margin="normal"
            />

            <Box sx={{ mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Creating Profile...' : 'Create Profile'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default CreateOrganizerProfile; 